import React, { useContext, useEffect, useMemo } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";

import { ClavaContext } from "../../../config/contexts";
import { selectMatchDays, selectSportsType } from "../../../selectors";
import { fetchMatchDays } from "../../../store/actions/matchActions";
import { AppDispatch } from "../../../store";
import {
  allowedViews,
  pathHome,
  pathHomeDate,
  pathMatchView,
  pathTeam,
  pathTeamView,
  toYmd,
  type View
} from "../../../config/routes";
import { formatYmdLocal, isFiniteDate, toLocalMidnight } from "../../../config/utils";

function isValidDate(d: unknown): d is Date {
  return d instanceof Date && isFiniteDate(d);
}

export const HomeDefaultRedirect: React.FC = () => {
  const { aoi } = useContext(ClavaContext);
  const sports = useSelector(selectSportsType);
  const matchDays = useSelector(selectMatchDays);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if ((matchDays?.length ?? 0) === 0 && aoi && sports) {
      fetchMatchDays(dispatch, aoi, new Date(), "today", sports);
    }
  }, [aoi, sports, matchDays, dispatch]);

  useEffect(() => {
    if ((matchDays?.length ?? 0) === 0) return;

    const isHomeRoot = /^\/home\/?$/.test(location.pathname);
    if (!isHomeRoot) return;

    const today = toLocalMidnight(new Date());
    const validDays = (matchDays ?? [])
      .map((d) => toLocalMidnight(new Date(d)))
      .filter(isValidDate)
      .sort((a, b) => a.getTime() - b.getTime());

    if (validDays.length === 0) return;

    const todayYmd = formatYmdLocal(today);
    const hasToday = validDays.some((d) => formatYmdLocal(d) === todayYmd);

    let target: Date;
    if (hasToday) {
      target = today;
    } else {
      const future = validDays.filter((d) => d.getTime() >= today.getTime());
      target = future[0] ?? today;
    }

    if (target) {
      navigate(pathHomeDate(toYmd(target)), { replace: true });
    }
  }, [matchDays, navigate, location.pathname]);

  return null;
};

export const MatchDefaultRedirect: React.FC = () => {
  const { matchId } = useParams();
  const target = useMemo(() => {
    const id = matchId ? Number(matchId) : undefined;
    return id != null && Number.isFinite(id)
      ? pathMatchView(id, "highlights")
      : pathHome();
  }, [matchId]);

  return <Navigate to={target} replace />;
};

const isAllowedView = (value?: string): value is View =>
  !!value && allowedViews.includes(value as View);

export const TeamLegacyRedirect: React.FC = () => {
  const { teamId, view } = useParams<{ teamId?: string; view?: string }>();
  const target = useMemo(() => {
    const id = teamId ? Number(teamId) : undefined;
    if (id == null || !Number.isFinite(id)) return pathHome();
    if (isAllowedView(view)) return pathTeamView(id, view);
    return pathTeam(id);
  }, [teamId, view]);

  return <Navigate to={target} replace />;
};

